import { WEB_MODULE_PERMISSIONS, type WebModule } from '../types/fleet'

type BrowserSessionProfile = Record<string, unknown> & {
  role?: string[]
  username: string
}

export type BrowserSession = {
  access_token?: string
  id_token?: string
  refresh_token?: string
  expires_at?: number
  profile?: BrowserSessionProfile
}

export function getSession() {
  const now = Math.floor(Date.now() / 1000)
  const sessions = Object.keys(sessionStorage)
    .filter((key) => key.includes('oidc.user'))
    .map((key) => {
      try {
        const session = JSON.parse(sessionStorage[key]) as BrowserSession
        return session.expires_at && session.expires_at > now ? session : null
      } catch {
        return null
      }
    })
    .filter(Boolean)

  if (sessions.length === 0) return null

  return sessions.reduce((latest, current) => (current.expires_at! > latest.expires_at! ? current : latest))
}

export function getAccessToken() {
  return getSession()?.access_token ?? null
}

export function getUserInfo() {
  return getSession()?.profile ?? null
}

export function decodeJwt<T = any>(authorization?: string): T | null {
  const token = authorization?.replace(/^Bearer\s+/i, '')
  const payload = token?.split('.')[1]
  if (!payload) return null

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes))
  } catch {
    return null
  }
}

export function getUserEmail(req: any) {
  const claims = decodeJwt(req.headers?.authorization)
  return req.user?.email ?? claims?.email
}

export function getUserId(req: any): string | undefined {
  return decodeJwt(req.headers?.authorization)?.sub
}

export function getSessionId(req: any): string | undefined {
  return decodeJwt(req.headers?.authorization)?.sid
}

export function getUserName(req: any) {
  const claims = decodeJwt(req.headers?.authorization)
  return req.user?.name ?? claims?.name
}

export function getUserOrganisationIds(req: any): string[] {
  return decodeJwt(req.headers?.authorization)?.organisations ?? []
}

export function userHasAccessToOrganisation(req: any): boolean {
  const organisationId = req.params?.organisationId
  if (!organisationId) return false

  return getUserOrganisationIds(req).includes(organisationId)
}

export function getUserGroupIds(req: any): string[] {
  const groupIds = decodeJwt(req.headers?.authorization)?.groupIds
  return Array.isArray(groupIds) ? groupIds : []
}

export function userHasWebModuleAccess(req: any, webModule: WebModule): boolean {
  const allowedRoles: readonly string[] = WEB_MODULE_PERMISSIONS[webModule]
  return getUserGroupIds(req).some((groupId) => allowedRoles.includes(groupId))
}

export function requireAccessHandler(webModule: WebModule) {
  return (req: any) => {
    if (!userHasWebModuleAccess(req, webModule)) throw accessError('No access')

    //for demo purposes
    return

    // const organisationId = req.params?.organisationId
    // if (organisationId && !userHasAccessToOrganisation(req)) {
    //   throw accessError('No access to the requested organisation')
    // }
  }
}

export function getCommerceUserHeaders(req: any) {
  const { organisationId } = req.params || {}
  return {
    'Content-Type': 'application/json',
    Authorization: req.headers?.authorization,
    ...(organisationId && { 'Current-User-Organisation': organisationId }),
  }
}

function accessError(message: string) {
  return Object.assign(new Error(message), { error: message })
}
